import { FileText } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { DocumentTemplatesPage } from '@/pages/settings/document-templates';
import type { PDFBuilderLocationState } from '@/pages/settings/pdf-builder';
import { PDFBuilderPage } from '@/pages/settings/pdf-builder';
import type { PDFWizardData } from '@/pages/settings/pdf-wizard';
import { PDFWizardPage } from '@/pages/settings/pdf-wizard';
import { TemplateEditorPage } from '@/pages/settings/template-editor';
import { ModeProvider } from '@/providers/ModeProvider';
import type { Template } from '@/types/template';

type EditorState =
  | { open: false }
  | { open: true; editorType: 'email'; mode: 'create'; template?: Template; duplicateFromId?: string }
  | { open: true; editorType: 'email'; mode: 'edit'; template: Template }
  | { open: true; editorType: 'pdfWizard' }
  | { open: true; editorType: 'pdfBuilder'; wizardData: PDFWizardData }
  | { open: true; editorType: 'pdfBuilder'; locationState: PDFBuilderLocationState };

export function Step4Templates() {
  const [setupNow, setSetupNow] = useState(false);
  const [editorState, setEditorState] = useState<EditorState>({ open: false });
  const [pdfBuilderKey, setPdfBuilderKey] = useState(0);

  const openCreateEmail = useCallback(() => {
    setEditorState({ open: true, editorType: 'email', mode: 'create' });
  }, []);

  const openCreatePDF = useCallback(() => {
    setEditorState({ open: true, editorType: 'pdfWizard' });
  }, []);

  const openEdit = useCallback((template: Template) => {
    if (template.type === 'statement') {
      setPdfBuilderKey((k) => k + 1);
      setEditorState({
        open: true,
        editorType: 'pdfBuilder',
        locationState: { template: template as unknown as Record<string, unknown>, mode: 'edit' },
      });
    } else {
      setEditorState({ open: true, editorType: 'email', mode: 'edit', template });
    }
  }, []);

  const openDuplicate = useCallback((template: Template) => {
    if (template.type === 'statement') {
      setPdfBuilderKey((k) => k + 1);
      setEditorState({
        open: true,
        editorType: 'pdfBuilder',
        locationState: {
          template: { ...template, name: `${template.name} (COPY)` } as unknown as Record<string, unknown>,
          mode: 'create',
          duplicateFromId: template.id,
        },
      });
    } else {
      setEditorState({
        open: true,
        editorType: 'email',
        mode: 'create',
        template: { ...template, name: `${template.name} (COPY)` },
        duplicateFromId: template.id,
      });
    }
  }, []);

  const closeEditor = useCallback(() => {
    setEditorState({ open: false });
  }, []);

  const handleSaved = useCallback((_savedTemplate: Template, _mode: 'create' | 'edit') => {
    setEditorState({ open: false });
  }, []);

  const handlePDFWizardContinue = useCallback((data: PDFWizardData) => {
    setEditorState({ open: true, editorType: 'pdfBuilder', wizardData: data });
  }, []);

  const handlePDFDuplicate = useCallback((state: PDFBuilderLocationState) => {
    setPdfBuilderKey((k) => k + 1);
    setEditorState({ open: true, editorType: 'pdfBuilder', locationState: state });
  }, []);

  const renderEditor = () => {
    if (!editorState.open) return null;

    switch (editorState.editorType) {
      case 'email':
        return (
          <ModeProvider>
            <TemplateEditorPage
              inlineMode={{
                template: editorState.template,
                mode: editorState.mode,
                duplicateFromId: editorState.mode === 'create' ? editorState.duplicateFromId : undefined,
                onBack: closeEditor,
                onSaved: handleSaved,
              }}
            />
          </ModeProvider>
        );
      case 'pdfWizard':
        return (
          <ModeProvider>
            <PDFWizardPage
              inlineMode={{
                onBack: closeEditor,
                onContinue: handlePDFWizardContinue,
              }}
            />
          </ModeProvider>
        );
      case 'pdfBuilder': {
        const initialState =
          'locationState' in editorState
            ? editorState.locationState
            : {
                templateName: editorState.wizardData.templateName,
                templateType: editorState.wizardData.templateType,
                description: editorState.wizardData.description,
              };
        return (
          <ModeProvider>
            <PDFBuilderPage
              key={pdfBuilderKey}
              inlineMode={{
                initialState,
                onBack: closeEditor,
                onSaved: handleSaved,
                onDuplicate: handlePDFDuplicate,
              }}
            />
          </ModeProvider>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Step header — hidden when the inline editor is open */}
      {!editorState.open && (
        <div className="mb-6 shrink-0">
          <h2 className="text-xl font-semibold mb-1">Templates</h2>
          <p className="text-sm text-muted-foreground">Review and create document templates for your workflows.</p>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {editorState.open ? (
          renderEditor()
        ) : setupNow ? (
          <ModeProvider>
            <DocumentTemplatesPage
              onCreateEmail={openCreateEmail}
              onCreatePDF={openCreatePDF}
              onEditTemplate={openEdit}
              onDuplicateTemplate={openDuplicate}
            />
          </ModeProvider>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10 text-primary mb-6">
              <FileText className="h-10 w-10" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No templates set up yet</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              Templates are <strong>not mandatory</strong> — you can set up the rest of Access Collect without them.
              When you're ready, they can also be created from <strong>Settings → Templates</strong>.
            </p>
            <Button onClick={() => setSetupNow(true)} className="gap-2">
              <FileText className="h-4 w-4" />
              Set Up Templates Now
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
